// /mnt/mantenimientos/frontend/src/modules/MonitorizacionModule.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isOperationalEquipment } from "../utils/equipmentLifecycle";
import { useCliente } from "../context/ClienteContext";
import { ModuleWorkspace } from "../components/workspace/ModuleWorkspace";

import PcMonitorList from "./monitorizacion/PcMonitorList";
import PcMonitorDetail from "./monitorizacion/PcMonitorDetail";
import PfSenseMonitorPanel from "./monitorizacion/PfSenseMonitorPanel";
import NativeMonitorPanel from "./monitorizacion/NativeMonitorPanel";
import { isTecnico } from "../utils/auth";
import { MODULE_IDS } from "./moduleIds";

import "../styles/modules/monitorizacion.css";

const DETAIL_TABS = new Set([
  "resumen",
  "interfaces",
  "servicios",
  "discos",
  "problemas",
]);
const safeDetailTab = (value) => (DETAIL_TABS.has(value) ? value : "resumen");

export default function MonitorizacionModule() {
  const {
    clienteSeleccionadoId,
    pcActivo,
    pcActual,
    setPcActivo,
    ordenadores,
    zabbixByPc,
    loadingZabbix,
    refreshZabbixCliente,
    setActiveModule,
    effectiveAccess,
  } = useCliente();
  const [monitorView, setMonitorView] = useState("native");
  const equiposOperativos = useMemo(
    () => (ordenadores || []).filter(isOperationalEquipment),
    [ordenadores]
  );

  const identity =
    localStorage.getItem("email") || localStorage.getItem("usuario") || "user";
  const workspaceKey = `workspace:${identity}:monitorizacion:${clienteSeleccionadoId || "none"}`;
  const restoredKey = useRef("");

  const readSaved = useCallback(
    (field, fallback) => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(workspaceKey) || "{}");
        return saved[field] ?? fallback;
      } catch {
        return fallback;
      }
    },
    [workspaceKey],
  );

  const [detailTab, setDetailTab] = useState(() =>
    safeDetailTab(readSaved("detailTab", "resumen")),
  );
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  // ✅ Hook SIEMPRE se llama (no condicional)
  const selectedEntry = useMemo(() => {
    if (!pcActivo) return null;
    return zabbixByPc?.[String(pcActivo)] || null;
  }, [pcActivo, zabbixByPc]);

  const selectedIndex = useMemo(
    () => equiposOperativos.findIndex((pc) => String(pc.id) === String(pcActivo)),
    [equiposOperativos, pcActivo],
  );

  useEffect(() => {
    if (!equiposOperativos.length || restoredKey.current === workspaceKey) return;
    const savedPc = readSaved("pcId", null);
    if (
      savedPc &&
      equiposOperativos.some((pc) => String(pc.id) === String(savedPc))
    ) {
      setPcActivo(savedPc);
    }
    setDetailTab(safeDetailTab(readSaved("detailTab", "resumen")));
    setMobileDetailOpen(false);
    restoredKey.current = workspaceKey;
  }, [equiposOperativos, readSaved, setPcActivo, workspaceKey]);

  useEffect(() => {
    if (restoredKey.current !== workspaceKey) return;
    sessionStorage.setItem(
      workspaceKey,
      JSON.stringify({ pcId: pcActivo, detailTab }),
    );
  }, [workspaceKey, pcActivo, detailTab]);

  const selectPc = (pcId) => {
    setPcActivo(pcId);
    setMobileDetailOpen(true);
  };

  const selectRelativePc = (offset) => {
    if (!equiposOperativos.length) return;
    const current = selectedIndex >= 0 ? selectedIndex : 0;
    const next = Math.max(
      0,
      Math.min(equiposOperativos.length - 1, current + offset),
    );
    setPcActivo(equiposOperativos[next].id);
  };

  const canOpenModule = (moduleId) =>
    !isTecnico() || effectiveAccess.moduleKeys.includes(moduleId);

  // ✅ Ahora sí: return temprano, después de hooks
  if (!clienteSeleccionadoId) {
    return (
      <div className="card">
        Selecciona un cliente para ver la monitorización.
      </div>
    );
  }

  if (monitorView === "native") {
    return <div className="mon-module"><div className="mon-source-tabs mp-source-switch"><button className="button primary">Monitorización · Megaprint</button><button className="button secondary" onClick={() => setMonitorView("equipos")}>Equipos · Zabbix</button><button className="button secondary" onClick={() => setMonitorView("firewalls")}>Firewalls · Megaprint</button></div><NativeMonitorPanel clientId={clienteSeleccionadoId} onOpenEquipment={canOpenModule(MODULE_IDS.EQUIPOS) ? (id) => { setPcActivo(id); setActiveModule(MODULE_IDS.EQUIPOS); } : null} /></div>;
  }

  if (monitorView === "firewalls") {
    return <div className="mon-module">
      <div className="mon-source-tabs mp-source-switch"><button className="button secondary" onClick={() => setMonitorView("native")}>Monitorización · Megaprint</button><button className="button secondary" onClick={() => setMonitorView("equipos")}>Equipos · Zabbix</button><button className="button primary">Firewalls · Megaprint</button></div>
      <PfSenseMonitorPanel clientId={clienteSeleccionadoId} />
    </div>;
  }

  return (<div className="mon-module">
    <div className="mon-source-tabs mp-source-switch"><button className="button secondary" onClick={() => setMonitorView("native")}>Monitorización · Megaprint</button><button className="button primary">Equipos · Zabbix</button><button className="button secondary" onClick={() => setMonitorView("firewalls")}>Firewalls · Megaprint</button></div>
    <ModuleWorkspace
      ariaLabel="Monitorización del cliente"
      detailOpen={mobileDetailOpen}
      onBackToList={() => setMobileDetailOpen(false)}
      detailKey={pcActivo}
      list={
        <PcMonitorList
          key={workspaceKey}
          ordenadores={equiposOperativos}
          zabbixByPc={zabbixByPc}
          pcActivo={pcActivo}
          setPcActivo={selectPc}
          storageKey={`${workspaceKey}:list`}
        />
      }
      detail={
        <PcMonitorDetail
          pcActual={pcActual}
          zabbixEntry={selectedEntry}
          tab={detailTab}
          setTab={setDetailTab}
          position={selectedIndex >= 0 ? selectedIndex + 1 : 0}
          total={equiposOperativos.length}
          onPrevious={selectedIndex > 0 ? () => selectRelativePc(-1) : null}
          onNext={
            selectedIndex >= 0 && selectedIndex < equiposOperativos.length - 1
              ? () => selectRelativePc(1)
              : null
          }
          onRefreshZabbix={() => refreshZabbixCliente && refreshZabbixCliente()}
          loadingZabbix={loadingZabbix}
          onOpenEquipment={
            canOpenModule(MODULE_IDS.EQUIPOS)
              ? () => setActiveModule(MODULE_IDS.EQUIPOS)
              : null
          }
          onOpenMaintenance={
            canOpenModule(MODULE_IDS.MANTENIMIENTOS)
              ? () => setActiveModule(MODULE_IDS.MANTENIMIENTOS)
              : null
          }
        />
      }
    />
  </div>);
}
